import './RecentHistoryMenu.scss';

import {
  mdiAccountMultipleOutline,
  mdiFolderOutline,
  mdiHistory,
  mdiInboxOutline,
  mdiMapMarkerOutline,
  mdiPackageVariantClosed,
  mdiWrenchOutline
} from '@mdi/js';
import { Popover } from 'antd';
import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import type { RecentHistoryEntry } from './recentHistory';
import { useRecentHistory } from './RecentHistoryProvider';

const typeIcons: Record<RecentHistoryEntry['type'], string> = {
  order: mdiFolderOutline,
  intervention: mdiMapMarkerOutline,
  externalRequest: mdiInboxOutline,
  portalRequest: mdiInboxOutline,
  serviceObject: mdiWrenchOutline,
  customer: mdiAccountMultipleOutline,
  material: mdiPackageVariantClosed,
  warehouse: mdiPackageVariantClosed,
  priceList: mdiPackageVariantClosed,
  adminCompany: mdiAccountMultipleOutline,
  adminFeatureFlag: mdiFolderOutline,
  adminSupportTicket: mdiInboxOutline
};

const typeLabels = {
  order: 'navigation_history_type_order',
  intervention: 'navigation_history_type_intervention',
  externalRequest: 'navigation_history_type_external_request',
  portalRequest: 'navigation_history_type_portal_request',
  serviceObject: 'navigation_history_type_service_object',
  customer: 'navigation_history_type_customer',
  material: 'navigation_history_type_material',
  warehouse: 'navigation_history_type_warehouse',
  priceList: 'navigation_history_type_price_list'
};

interface Props {
  expanded?: boolean;
  mobile?: boolean;
  variant?: 'sidebar' | 'header';
  onOpenChange?: (open: boolean) => void;
  onNavigate?: () => void;
}

const noop = () => {};

/** Recent detail links share the shell's popup behavior on desktop and mobile. */
const RecentHistoryMenu = ({
  expanded = false,
  mobile = false,
  variant = 'sidebar',
  onOpenChange = noop,
  onNavigate = noop
}: Props) => {
  const header = variant === 'header';
  const { entries, clearHistory } = useRecentHistory();
  const { t, tCommon } = useCustomTranslation();
  const adminTypeLabels = {
    adminCompany: t('mainMenu.companies'),
    adminFeatureFlag: t('mainMenu.featureFlags'),
    adminSupportTicket: t('mainMenu.supportTickets')
  };
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const label = tCommon('navigation_history');

  useEffect(() => {
    setOpen(false);
    onOpenChange(false);
  }, [pathname, mobile, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      onOpenChange(false);
      trigger.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  return (
    <div className={`RecentHistoryMenu_anchor${header ? ' is-header' : ''}`}>
      <Popover
        trigger="click"
        arrow={false}
        classNames={{ root: `RecentHistoryMenu${!header && !mobile ? ' is-desktop' : ''}` }}
        getPopupContainer={header || mobile ? undefined : (node) => node.parentElement!}
        placement={header ? 'bottomRight' : mobile ? 'topLeft' : 'rightBottom'}
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          onOpenChange(value);
        }}
        title={label}
        content={
          <div className="RecentHistoryMenu_content">
            {entries.length ? (
              <>
                <ul className="RecentHistoryMenu_list" aria-label={label}>
                  {entries.map((entry) => (
                    <li key={`${entry.type}:${entry.id}`}>
                      <Link
                        to={entry.path}
                        onClick={() => {
                          setOpen(false);
                          onOpenChange(false);
                          onNavigate();
                        }}
                      >
                        <Icon path={typeIcons[entry.type]} className="RecentHistoryMenu_typeIcon" />
                        <div className="RecentHistoryMenu_entryText">
                          <span>{entry.title}</span>
                          <small>
                            {entry.type in adminTypeLabels
                              ? adminTypeLabels[entry.type as keyof typeof adminTypeLabels]
                              : tCommon(typeLabels[entry.type as keyof typeof typeLabels])}
                          </small>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Button type="text" className="RecentHistoryMenu_clear" onClick={clearHistory}>
                  {tCommon('navigation_history_clear')}
                </Button>
              </>
            ) : (
              <p className="RecentHistoryMenu_empty">{tCommon('navigation_history_empty')}</p>
            )}
          </div>
        }
      >
        <Button
          ref={trigger}
          type="text"
          className="AppNavigation_history"
          aria-label={tCommon('navigation_history_trigger')}
          aria-expanded={open}
        >
          <Icon path={mdiHistory} />
          {(header || expanded) && <span>{tCommon('navigation_history_trigger')}</span>}
        </Button>
      </Popover>
    </div>
  );
};

export default RecentHistoryMenu;
